import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useApplyModal } from '../../features/apply/apply-context';
import { Container } from '../../shared/ui';
import { legalDocs } from '../../shared/lib/legal-docs';
import { StoreLinks } from '../../shared/ui/store-links';
import footerQr from '../../shared/assets/images/footer-qr.webp';
import logoInverse from '../../shared/assets/images/logo-inverse.svg';
import { PaymentSystems } from './payment-systems';
import type { ReactNode } from 'react';

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      <p className="font-involve text-base font-semibold text-white lg:text-xl">{title}</p>
      <div className="text-text-placeholder flex flex-col gap-2 text-sm lg:text-base">
        {children}
      </div>
    </div>
  );
}

export function Footer() {
  const { t } = useTranslation();
  const { openApplyModal } = useApplyModal();

  return (
    <footer className="bg-ink-900">
      <Container className="flex flex-col gap-6 py-10 lg:gap-10">
        <section
          id="download-app"
          tabIndex={-1}
          aria-labelledby="download-app-title"
          className="flex scroll-mt-28 flex-col items-start justify-between gap-8 lg:flex-row lg:items-center"
        >
          <div className="flex flex-col gap-4 lg:gap-6">
            <div className="flex flex-col gap-2">
              <h2
                id="download-app-title"
                className="text-[18px] font-medium leading-normal tracking-[-0.36px] text-white lg:text-2xl lg:tracking-normal"
              >
                {t('footer.app.title')}
              </h2>
              <p className="text-text-placeholder max-w-xl text-sm lg:text-base">
                {t('footer.app.description')}
              </p>
            </div>
            <StoreLinks className="text-text-placeholder" />
          </div>
          {/* The design drops the QR on phones — scanning a code on the same
              device it is displayed on is pointless, and it costs a third of the
              viewport (Figma node 5663:15271). */}
          <div className="bg-surface-muted hidden shrink-0 overflow-hidden rounded-2xl lg:block">
            <img src={footerQr} alt="" className="size-40" loading="lazy" />
          </div>
        </section>

        <hr className="border-white/10" />

        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
          {/* Brand restatement is desktop-only in the design: on phones it sits
              directly under the same copy in the block above. */}
          <div className="hidden flex-col gap-2 lg:flex">
            <img
              src={logoInverse}
              alt={t('footer.brand.title')}
              className="h-10 w-auto self-start"
              loading="lazy"
            />
            <p className="text-text-placeholder max-w-xs text-base">
              {t('footer.brand.description')}
            </p>
            {/* On desktop the payment marks belong to the brand column; on
                phones the design moves them to the bottom bar instead. */}
            <PaymentSystems className="mt-2" />
          </div>

          {/* Two columns on phones (Бизнесу | Приложение, then Контакты on its own
              row), a single wrapping row from `lg`. */}
          <div className="grid grid-cols-2 gap-x-10 gap-y-10 lg:flex lg:flex-wrap lg:gap-x-16 lg:gap-y-8">
            <FooterColumn title={t('footer.business.title')}>
              <Link to="/business" hash="how-it-works" className="hover:text-white">
                {t('footer.business.howItWorks')}
              </Link>
              <Link to="/business" hash="advantages" className="hover:text-white">
                {t('footer.business.advantages')}
              </Link>
              <button type="button" onClick={openApplyModal} className="text-left hover:text-white">
                {t('footer.business.apply')}
              </button>
            </FooterColumn>

            <FooterColumn title={t('footer.appNav.title')}>
              <StoreLinks variant="text" />
            </FooterColumn>

            <FooterColumn title={t('footer.contacts.title')}>
              <a
                href={`tel:${t('footer.contacts.phone').replace(/[^\d+]/g, '')}`}
                className="hover:text-white"
              >
                {t('footer.contacts.phone')}
              </a>
              <a href={`mailto:${t('footer.contacts.email')}`} className="hover:text-white">
                {t('footer.contacts.email')}
              </a>
            </FooterColumn>
          </div>
        </div>

        <hr className="border-white/10 lg:hidden" />

        {/* Centred row between the divider and the legal links — the phone
            placement of the same marks shown in the brand column on desktop. */}
        <PaymentSystems className="justify-center lg:hidden" />

        {/* `flex-col-reverse` puts the legal links above the copyright line on
            phones, which is the order the design uses; from `sm` it is a single
            row with the copyright back on the left. */}
        <div className="text-text-placeholder flex flex-col-reverse gap-6 text-sm sm:flex-row sm:items-center sm:justify-between">
          <p>{t('footer.copyright')}</p>
          {/* Three links no longer fit one row on a narrow desktop, so the row
              wraps; the horizontal gap stays as designed while wrapped lines get a
              tighter vertical one. */}
          <div className="flex flex-wrap gap-x-16 gap-y-2">
            {/* All three documents are HTML pages now, not PDFs. They live
                outside the router (static files served by nginx), so these are
                plain same-tab `<a>`s — a TanStack `Link` would try to resolve
                them as routes. Each page carries its approved PDF as a
                download. */}
            <a href={legalDocs.offer.path} className="hover:text-white">
              {t('footer.offer')}
            </a>
            <a href={legalDocs.privacy.path} className="hover:text-white">
              {t('footer.privacy')}
            </a>
            <a href={legalDocs.cardPayment.path} className="hover:text-white">
              {t('footer.cardPayment')}
            </a>
          </div>
        </div>
      </Container>
    </footer>
  );
}
